import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { Modal } from '../Modal/Modal';
import { useSharedTranslation } from '../hooks/useSharedTranslation';
import { ScheduleEntry } from 'us-web-services';
import { bookingStatusKey, formatTime } from './utils';
import { DateFormat, formatDateToString } from '../utils/date';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  booking: ScheduleEntry['booking'] | null;
  namespace?: string;
  showContactDetails?: boolean;
}

const bookingMapHref = (
  booking: ScheduleEntry['booking'] | null,
): string => {
  const addr = booking?.address;
  if (!addr?.street && !addr?.city) return '#';
  const q = [addr?.street, addr?.city, addr?.state].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

const formatStartDateTime = (iso?: string | Date): string => {
  if (!iso) return '';
  return formatDateToString(iso, DateFormat.DATE_WEEKDAY_TIME_FORMAT);
};

export const BookingPreviewModal = ({
  isOpen,
  onClose,
  booking,
  namespace,
  showContactDetails,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);
  const statusCode = booking?.status?.code;
  const pending =
    statusCode === 'pending' ||
    statusCode === 'parent_booking' ||
    statusCode === 'parent_reschedule' ||
    statusCode === 'sitter_reschedule';
  const ctaLabel = pending && statusCode !== 'sitter_reschedule'
    ? t('pages.calendar.viewRequest')
    : t('pages.calendar.viewBooking');

  const headerCls = pending
    ? 'bg-honey-50 text-neutral-800'
    : 'bg-sapphire-50 text-neutral-800';

  const timeRange = t('common.timeRange', {
    start: formatStartDateTime(booking?.start),
    end: formatTime(booking?.end),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      removePadding
      className='w-full max-w-md'
    >
      <div className={`${headerCls} px-4 py-3 sm:rounded-t-lg relative flex items-center`}>
        <h2 className='flex-1 text-center font-bold text-base'>
          {booking ? t(bookingStatusKey(booking)) : ''}
        </h2>
        <button
          type='button'
          onClick={onClose}
          aria-label={t('common.close')}
          className='absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity'
        >
          <FontAwesomeIcon icon={faXmark} className='w-5 h-5' />
        </button>
      </div>

      {booking && (
        <div className='p-4 sm:p-6'>
          {showContactDetails ? (
            <div className='flex items-start gap-3 mb-4'>
              {booking.parent?.pictureUrl && (
                <img
                  id={`booking-modal-parent-picture-${booking.parent.id ?? ''}`}
                  alt={t('pages.calendar.profilePhotoFor', { name: booking.parent.name ?? '' })}
                  src={booking.parent.pictureUrl}
                  className='w-20 h-20 rounded object-cover flex-shrink-0'
                />
              )}
              <div className='min-w-0 text-sm'>
                <div className='font-semibold text-base'>{booking.parent?.name}</div>
                {booking.address && (
                  <a
                    href={bookingMapHref(booking)}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline'
                  >
                    {booking.address.street}
                    {booking.address.street && <br />}
                    {booking.address.city}
                    {booking.address.city && booking.address.state && ', '}
                    {booking.address.state}
                  </a>
                )}
                <div className='mt-1'>{timeRange}</div>
                <div className='mt-1'>
                  {t('pages.calendar.ratePerHour', { rate: booking.rate })}
                  <br />
                  {t('pages.calendar.childCount', { count: booking.numberOfChildren ?? 0 })}
                  {booking.kidsAges && (
                    <span> {t('pages.calendar.kidsAgesParenthetical', { ages: booking.kidsAges })}</span>
                  )}
                  <br />
                  {booking.parent?.phone && (
                    <>
                      {booking.parent.phone}
                      <br />
                    </>
                  )}
                  {t('pages.calendar.payVia', { paymentType: booking.paymentType })}
                </div>
              </div>
            </div>
          ) : (
            <div className='mb-4 text-sm'>{timeRange}</div>
          )}
          <a
            id='booking-details-button'
            href={`/app/bookings/${booking.id}`}
            className='block w-full text-center text-white hover:text-white py-2 px-4 rounded-full font-semibold bg-sapphire hover:bg-sapphire-700 transition-all duration-500'
          >
            {ctaLabel}
          </a>
        </div>
      )}
    </Modal>
  );
};
